/**
 * TROPAC Dimension Range — owned, update-proof dimension filter slider.
 *
 * Origin: #437 (dual-handle slider), #606 (mid-drag submit safety),
 * #1406 (typed min/max fields). This logic ORIGINALLY shipped inside the
 * Athens vendor file `assets/facets.js`, which made every Athens theme
 * update a guaranteed merge conflict. Per theme/CUSTOMISATIONS.md it now
 * lives entirely in this owned `tropac-` asset. The only vendor-file
 * footprint left is the thin whitelist + `<dimension-range>` mount in
 * `snippets/collection-filters.liquid` (a snippet, itself a customisation
 * surface Athens does not churn the same way).
 *
 * ------------------------------------------------------------------------
 * What it does
 * ------------------------------------------------------------------------
 * `<dimension-range>` is a custom element rendered ABOVE the discrete
 * checkbox list for the tropac.width_mm / height_mm / depth_mm metafield
 * filters. Shopify Search & Discovery returns these as filter.type='list'
 * (no native 'range' type for generic numeric metafields), so the underlying
 * form stays a set of checkbox inputs — this component just provides the
 * slider + typed-field UI that programmatically ticks the in-range checkboxes.
 * Form submission goes through the existing Athens FacetFiltersForm unchanged;
 * we never re-implement the form. Owned JS calling into the vendor form API
 * (tick a checkbox + dispatch its `input`) is fine — the LOGIC just doesn't
 * live inside facets.js anymore.
 *
 * Contract preserved from the vendor version (do not "simplify" away):
 *   - Dual thumbs + a DISCRETE S&D value list (NOT continuous gte/lte URL
 *     semantics). We snap to the nearest real stock value.
 *   - #606 mid-drag safety: intermediate `input` events stopPropagation so
 *     the form does not submit / re-render / destroy this element mid-drag;
 *     the real commit fires from a ticked checkbox on thumb release / field
 *     blur / Enter.
 *   - Crossover PUSH (not clamp-active): dragging one thumb past the other
 *     pushes the other along (range sticks to the dragged edge). When the two
 *     thumbs stack on the same pixel (min===max) MAX is on top by default so
 *     the first drag-right reopens the range; the pressed thumb is raised on
 *     pointerdown. Old code clamped the active thumb back, which deadlocked
 *     at min===max. See #equal-thumb fix + theme/CUSTOMISATIONS.md.
 *
 * Load order note: this file is loaded (with `defer`) from
 * collection-filters.liquid alongside the facet markup. We define the custom
 * element idempotently (guarded by customElements.get) so that even if the
 * script races the markup, the browser upgrades any `<dimension-range>`
 * already parsed — no FOUC-break on Athens upgrade.
 */
(function () {
  'use strict';

  // Guard: if a previous (vendor) definition somehow already registered the
  // element (e.g. a stale facets.js during a partial deploy), don't redefine
  // and throw. Owned definition wins on a clean deploy where facets.js no
  // longer defines it.
  if (window.customElements && customElements.get('dimension-range')) {
    return;
  }

  // ── Pure crossover / z-order helpers ─────────────────────────────────────
  //
  // Extracted as standalone functions (not class methods) so the offline
  // gate script `scripts/_test_dim_range_crossover.mjs` can mirror them
  // verbatim and assert the push/crossover + equal-z contract without a
  // browser. Keep these in sync with that test file.
  //
  // pushCrossover: when the active thumb crosses the other, PUSH the other
  // to the active thumb's value instead of clamping the active thumb back.
  // This is what unbreaks the equal/deadlock state (old code did
  // `minVal = maxVal` / `maxVal = minVal` on the ACTIVE thumb, which forced
  // it back and deadlocked at min===max). `active` is 'min' or 'max'.
  function pushCrossover(active, minVal, maxVal) {
    if (active === 'min' && minVal > maxVal) {
      return { min: minVal, max: minVal };
    }
    if (active === 'max' && maxVal < minVal) {
      return { min: maxVal, max: maxVal };
    }
    return { min: minVal, max: maxVal };
  }

  // zOrderForEqual: when the two thumbs paint on the SAME pixel (min===max),
  // put MAX on top so the first drag-right grabs max and opens the range
  // outward (standard dual-range UX). Otherwise min on top so the left edge
  // stays grabbable in a normal range. Returns the z-index for each thumb.
  function zOrderForEqual(equal) {
    return equal ? { min: 1, max: 2 } : { min: 2, max: 1 };
  }

  class DimensionRange extends HTMLElement {
    constructor() {
      super();
      this.unit = this.dataset.unit || 'mm';
      this.label = this.dataset.label || '';
      this.checkboxes = [];
      this.values = [];
      this.min = 0;
      this.max = 0;
    }

    connectedCallback() {
      // The sibling .facet-options-list contains the actual checkbox set.
      // The Liquid renders <dimension-range> BEFORE the <ul>, so the
      // checkbox list is a following sibling under the same .facet-content.
      const parent = this.closest('.facet-content');
      if (!parent) return;
      const list = parent.querySelector('.facet-options-list');
      if (!list) return;

      this.checkboxes = Array.from(list.querySelectorAll('input[type="checkbox"]'));
      if (this.checkboxes.length === 0) return;

      // Numeric sort. Shopify returns values sorted alphabetically, which
      // breaks slider domain math (e.g. "100" sorts before "20"). Coerce
      // to Number first.
      this.values = this.checkboxes
        .map((cb) => ({ cb, num: Number(cb.value) }))
        .filter((v) => Number.isFinite(v.num))
        .sort((a, b) => a.num - b.num);

      if (this.values.length === 0) return;

      this.min = this.values[0].num;
      this.max = this.values[this.values.length - 1].num;

      // Initial selection: if any checkboxes are pre-checked (from URL params),
      // use their min/max as the current range. Otherwise default to full range.
      const checkedValues = this.values
        .filter((v) => v.cb.checked)
        .map((v) => v.num);
      this.currentMin =
        checkedValues.length > 0 ? Math.min(...checkedValues) : this.min;
      this.currentMax =
        checkedValues.length > 0 ? Math.max(...checkedValues) : this.max;

      this.render();
      // Hide the native checkbox list — slider replaces it visually
      list.classList.add('facet-options-list-hidden-by-slider');
    }

    render() {
      // #1406 (Alessandro request): typed min/max fields UNDER the slider so
      // buyers can punch in exact dimensions instead of only dragging thumbs.
      // The slider is the primary affordance, so the track/thumbs render FIRST
      // and the typed fields sit BELOW them (smaller/quieter — see CSS).
      this.innerHTML = `
        <div class="dimension-range">
          <div class="dimension-range-track">
            <div class="dimension-range-track-fill"></div>
            <input
              type="range"
              class="dimension-range-thumb dimension-range-thumb-min"
              min="${this.min}"
              max="${this.max}"
              value="${this.currentMin}"
              step="any"
              aria-label="Minimum ${this.label}"
            >
            <input
              type="range"
              class="dimension-range-thumb dimension-range-thumb-max"
              min="${this.min}"
              max="${this.max}"
              value="${this.currentMax}"
              step="any"
              aria-label="Maximum ${this.label}"
            >
          </div>
          <div class="dimension-range-fields">
            <div class="dimension-range-field">
              <label class="visually-hidden" for="${this.fieldId('min')}">Minimum ${this.label}</label>
              <input
                type="number"
                class="dimension-range-input dimension-range-input-min"
                id="${this.fieldId('min')}"
                min="${this.min}"
                max="${this.max}"
                value="${this.currentMin}"
                inputmode="numeric"
                aria-label="Minimum ${this.label}"
              >
              <span class="dimension-range-input-unit">${this.unit}</span>
            </div>
            <span class="dimension-range-fields-sep">–</span>
            <div class="dimension-range-field">
              <label class="visually-hidden" for="${this.fieldId('max')}">Maximum ${this.label}</label>
              <input
                type="number"
                class="dimension-range-input dimension-range-input-max"
                id="${this.fieldId('max')}"
                min="${this.min}"
                max="${this.max}"
                value="${this.currentMax}"
                inputmode="numeric"
                aria-label="Maximum ${this.label}"
              >
              <span class="dimension-range-input-unit">${this.unit}</span>
            </div>
          </div>
        </div>
      `;

      this.minThumb = this.querySelector('.dimension-range-thumb-min');
      this.maxThumb = this.querySelector('.dimension-range-thumb-max');
      this.trackFill = this.querySelector('.dimension-range-track-fill');
      this.minInput = this.querySelector('.dimension-range-input-min');
      this.maxInput = this.querySelector('.dimension-range-input-max');

      // stopPropagation on BOTH input and change: FacetFiltersForm listens
      // on `input` (facets.js top of file), so every drag pixel would
      // otherwise submit the form with checkboxes still un-ticked — the
      // form serialises CURRENT state at submit time, captures empty
      // dimension params, returns an unfiltered grid that re-renders the
      // sidebar HTML, which destroys this <dimension-range> element
      // mid-drag (#606). The slider's own commit step (onThumbCommit)
      // dispatches a fresh `input` event from a ticked checkbox to fire
      // the form submit with the correct state.
      const stopAndInput = (event) => {
        event.stopPropagation();
        this.onThumbInput(event);
      };
      const stopAndCommit = (event) => {
        event.stopPropagation();
        this.onThumbCommit();
        // Reset resting z-order after a commit (covers keyboard-driven
        // thumb changes that fire `change` without a pointerup).
        this.applyZOrder();
      };
      this.minThumb.addEventListener('input', stopAndInput);
      this.maxThumb.addEventListener('input', stopAndInput);
      this.minThumb.addEventListener('change', stopAndCommit);
      this.maxThumb.addEventListener('change', stopAndCommit);

      // Equal-stack / z-order (#equal-thumb bug): when min===max the two
      // full-width range inputs paint their thumb circle on the SAME pixel.
      // CSS keeps min on top (z-index:2) so only one knob is visible and it
      // reads as "right selector missing". We drive z-index from JS so:
      //   - at rest, when equal, MAX is on top (first drag-right opens the
      //     range outward) and otherwise MIN is on top (left edge grabbable);
      //   - while a thumb is being dragged (pointerdown -> release), THAT
      //     thumb is raised to z-index:4 so it stays the grab target even
      //     when it crosses the other. Covers mouse + touch + pen.
      const raiseActive = (event) => {
        this._activeThumb = event.currentTarget;
        event.currentTarget.style.zIndex = '4';
      };
      const lowerActive = () => {
        this._activeThumb = null;
        this.applyZOrder();
      };
      for (const thumb of [this.minThumb, this.maxThumb]) {
        thumb.addEventListener('pointerdown', raiseActive);
        thumb.addEventListener('mousedown', raiseActive);
        thumb.addEventListener('touchstart', raiseActive);
        thumb.addEventListener('pointerup', lowerActive);
        thumb.addEventListener('mouseup', lowerActive);
        thumb.addEventListener('touchend', lowerActive);
        thumb.addEventListener('pointercancel', lowerActive);
      }

      // Typed number fields follow the exact same #606 rules as the thumbs:
      //   - intermediate `input` events (each keystroke) stopPropagation so the
      //     form does NOT submit mid-typing and re-render/destroy this element.
      //   - commit (snap + tick checkboxes + fire form submit) happens ONLY on
      //     blur (`change`) or Enter, after we snap to a discrete stock value.
      const stopNumberInput = (event) => {
        event.stopPropagation();
      };
      const commitNumber = (event) => {
        event.stopPropagation();
        this.onNumberCommit(event.target);
      };
      this.minInput.addEventListener('input', stopNumberInput);
      this.maxInput.addEventListener('input', stopNumberInput);
      this.minInput.addEventListener('change', commitNumber);
      this.maxInput.addEventListener('change', commitNumber);
      const commitOnEnter = (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          this.onNumberCommit(event.target);
          event.target.blur();
        }
      };
      this.minInput.addEventListener('keydown', commitOnEnter);
      this.maxInput.addEventListener('keydown', commitOnEnter);

      this.updateTrackFill();
      this.applyZOrder();
    }

    // Apply at-rest z-order for the two stacked thumbs. While a thumb is
    // being dragged it is raised to z-index:4 by the pointerdown handler
    // and this method is NOT called mid-drag; it runs on render / commit /
    // pointer release to reset the resting order.
    applyZOrder() {
      if (!this.minThumb || !this.maxThumb) return;
      const order = zOrderForEqual(this.currentMin === this.currentMax);
      this.minThumb.style.zIndex = String(order.min);
      this.maxThumb.style.zIndex = String(order.max);
    }

    fieldId(which) {
      // Stable, collision-safe id from the human label (e.g. "Height").
      const slug = (this.label || 'dim')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      return `dimension-range-${slug || 'dim'}-${which}`;
    }

    onThumbInput(event) {
      let minVal = Number(this.minThumb.value);
      let maxVal = Number(this.maxThumb.value);

      // Snap each thumb to the nearest actual discrete value so the
      // readout matches what will be selected. Shopify only has discrete
      // values; an arbitrary slider position would mislead the user.
      minVal = this.snap(minVal);
      maxVal = this.snap(maxVal);

      // Crossover PUSH (not clamp-active): when the dragged thumb crosses
      // the other, push the other thumb to the dragged value so the range
      // sticks to the dragged edge. The old code clamped the ACTIVE thumb
      // back (`minVal = maxVal`), which at min===max forced it straight back
      // and deadlocked — you could never reopen a collapsed range by drag.
      const active = event.target === this.minThumb ? 'min' : 'max';
      const pushed = pushCrossover(active, minVal, maxVal);
      minVal = pushed.min;
      maxVal = pushed.max;
      // Reflect the moved OTHER thumb back into its input so it visually
      // follows. Do NOT reset the active thumb's input value mid-drag: it
      // already shows the raw drag position (readout/checkboxes use the
      // snapped currentMin/currentMax, matching the pre-fix behaviour).
      if (active === 'min') {
        if (Number(this.maxThumb.value) !== maxVal) this.maxThumb.value = maxVal;
      } else {
        if (Number(this.minThumb.value) !== minVal) this.minThumb.value = minVal;
      }

      this.currentMin = minVal;
      this.currentMax = maxVal;
      // Keep the typed number fields in sync while dragging thumbs.
      this.syncInputs();
      this.updateTrackFill();
    }

    onNumberCommit(sourceInput) {
      // Read the typed fields, snap to nearest discrete stock value, clamp to
      // the current domain, and push on crossover (min <= max). Empty /
      // invalid / NaN input is treated as "no change": we clamp back to the
      // current value rather than throwing or stripping every dimension tick
      // by accident.
      let minVal = this.parseInput(this.minInput, this.currentMin);
      let maxVal = this.parseInput(this.maxInput, this.currentMax);

      // Clamp into [min, max] domain before snapping.
      minVal = Math.min(Math.max(minVal, this.min), this.max);
      maxVal = Math.min(Math.max(maxVal, this.min), this.max);

      minVal = this.snap(minVal);
      maxVal = this.snap(maxVal);

      // Crossover PUSH: whichever field the user just committed wins; the
      // other yields to it. We use the committed source input (event.target),
      // NOT document.activeElement — on `change`/blur the focus has already
      // moved, so activeElement would be wrong and the push would pick the
      // wrong side.
      const active = sourceInput === this.minInput ? 'min' : 'max';
      const pushed = pushCrossover(active, minVal, maxVal);
      minVal = pushed.min;
      maxVal = pushed.max;

      this.currentMin = minVal;
      this.currentMax = maxVal;

      // Reflect snapped/clamped values back into thumbs + fields + fill, then
      // run the shared commit path (tick checkboxes + fire the form submit).
      this.minThumb.value = minVal;
      this.maxThumb.value = maxVal;
      this.syncInputs();
      this.updateTrackFill();
      this.applyZOrder();
      this.onThumbCommit();
    }

    parseInput(input, fallback) {
      // Return a finite number from the field, or the fallback for empty/invalid.
      const raw = (input.value || '').trim();
      if (raw === '') return fallback;
      const n = Number(raw);
      return Number.isFinite(n) ? n : fallback;
    }

    syncInputs() {
      // Push current committed values into the number fields.
      if (this.minInput) this.minInput.value = this.currentMin;
      if (this.maxInput) this.maxInput.value = this.currentMax;
    }

    onThumbCommit() {
      // Tick in-range checkboxes, untick out-of-range. Then dispatch
      // `input` (NOT `change`) from a ticked checkbox so Athens'
      // FacetFiltersForm picks it up — the form's debounced submit
      // listens for `input`, not `change` (facets.js line 31). #606.
      let anyChange = false;
      for (const { cb, num } of this.values) {
        const shouldBeChecked = num >= this.currentMin && num <= this.currentMax;
        if (cb.checked !== shouldBeChecked) {
          cb.checked = shouldBeChecked;
          anyChange = true;
        }
      }
      if (anyChange) {
        const last = this.values[this.values.length - 1].cb;
        last.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    snap(value) {
      // Snap to the nearest discrete value in the sorted set.
      let best = this.values[0].num;
      let bestDist = Math.abs(value - best);
      for (const { num } of this.values) {
        const d = Math.abs(value - num);
        if (d < bestDist) {
          best = num;
          bestDist = d;
        }
      }
      return best;
    }

    updateTrackFill() {
      if (!this.trackFill) return;
      const span = this.max - this.min;
      if (span <= 0) return;
      const leftPct = ((this.currentMin - this.min) / span) * 100;
      const rightPct = ((this.currentMax - this.min) / span) * 100;
      this.trackFill.style.left = leftPct + '%';
      this.trackFill.style.width = rightPct - leftPct + '%';
    }
  }

  customElements.define('dimension-range', DimensionRange);
})();
